import classnames from 'classnames';
import dynamic from 'next/dynamic';
import {
  Children,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import * as React from 'react';

import { Accordion } from '@/core/tamagoshi/Accordion/Accordion';
import type { AccordionItemToggleHandler } from '@/core/tamagoshi/Accordion/AccordionItem';

import type { FiltersPanelFooterProps } from './components/FiltersPanelFooter/FiltersPanelFooter';

import styles from './FiltersPanel.module.scss';

const DynamicFiltersPanelFooter = dynamic<FiltersPanelFooterProps>(
  () =>
    import('./components/FiltersPanelFooter/FiltersPanelFooter').then(
      (mod) => mod.FiltersPanelFooter,
    ),
  { ssr: false },
);

interface Props extends React.PropsWithChildren {
  className?: string;
  productsCount: number;
  open?: boolean;
  onClose?(event: MouseEvent): void;
}

interface EnrichedChildren {
  className: string;
}

export interface FiltersPanelContext {
  openedFilters: {
    [key: string]: boolean;
  };
  onToggleFilterBlock?: AccordionItemToggleHandler;
}

const FiltersPanelContext = createContext<FiltersPanelContext>({
  openedFilters: {},
});

export const useFiltersPanel = (
  name: string,
): [boolean, FiltersPanelContext['onToggleFilterBlock']] => {
  const context = useContext(FiltersPanelContext);

  if (!context) {
    throw new Error(`[FiltersPanel] Could not find required "openedFilters" object.
    <FiltersPanelProvider> needs to exist in the component ancestry.`);
  }

  return [context.openedFilters[name], context.onToggleFilterBlock];
};

let alreadyOpened = false;
async function disableBodyScrollByElement(targetElemnt: HTMLElement | Element) {
  const { disableBodyScroll } = await import('body-scroll-lock');
  disableBodyScroll(targetElemnt);
}
async function enableBodyScrollByElement(targetElemnt: HTMLElement | Element) {
  const { enableBodyScroll } = await import('body-scroll-lock');
  enableBodyScroll(targetElemnt);
}

/**
 * This is the mobile and desktop filters panel use for listing pages
 *
 * ## Usage
 * ```
 * <FiltersPanel activeFiltersCount={0} />
 * ```
 */
export const FiltersPanel: FunctionComponent<Props> = ({
  className,
  open = false,
  productsCount,
  children,
  onClose,
}) => {
  const [openedFilters, setOpenedFilters] = useState<{
    [key: string]: boolean;
  }>({});
  const rootClassnames = classnames(styles.root, className);

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (rootRef.current) {
      if (open) {
        alreadyOpened = true;
        disableBodyScrollByElement(rootRef.current);
      } else if (alreadyOpened) {
        enableBodyScrollByElement(rootRef.current);
      }
    }

    return () => {
      import('body-scroll-lock').then(({ clearAllBodyScrollLocks }) =>
        clearAllBodyScrollLocks(),
      );
    };
  }, [open, rootRef]);

  const handleFilterBlockToggle = useCallback<AccordionItemToggleHandler>(
    (event, id, isOpen) => {
      setOpenedFilters({ ...openedFilters, [id]: isOpen });
    },
    [openedFilters],
  );

  const contextValue = React.useMemo(
    () => ({
      openedFilters,
      onToggleFilterBlock: handleFilterBlockToggle,
    }),
    [handleFilterBlockToggle, openedFilters],
  );

  return (
    <FiltersPanelContext.Provider value={contextValue}>
      <section className={rootClassnames} data-testid="filters-panel">
        <div className={styles.bodyWrapper} ref={rootRef}>
          <Accordion className={styles.body} manual type="dividers">
            {Children.map(children, (child) => {
              if (!React.isValidElement<EnrichedChildren>(child)) {
                return child;
              }

              const filterBlock: ReactElement<EnrichedChildren> = child;
              return React.cloneElement(filterBlock, {
                className: classnames(child.props.className, styles.filter),
              });
            })}
          </Accordion>

          {open && (
            <DynamicFiltersPanelFooter
              className={styles.footer}
              count={productsCount}
              onClick={onClose}
            />
          )}
        </div>
      </section>
    </FiltersPanelContext.Provider>
  );
};

FiltersPanel.displayName = 'FiltersPanel';
